/**
 * Drawer — slides in from the right when any score is clicked.
 *
 * Content mirrors the v3 orbital element panel so clicking a Booster here and
 * clicking the same element in The Detail show the SAME thing:
 *   • Header: element name + close
 *   • Big score (one decimal) + classification label (muted)
 *   • GradientGauge with -100% / 0 / +100% scale (-100% / 0% for Price Deals)
 *   • Element description — the ELEMENT_DESCRIPTIONS sidebar text
 *   • "First Thoughts" card — the element's AI summary (falls back to the
 *     "awaiting responses" message), with a working "Analyse Again" button
 *   • Sticky footer: "View in the visualisation →" → The Detail
 *
 * ESC + backdrop-click close.
 */

import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { colourForScore } from '../config/elementColours';
import type { ScoreNode } from '../types';
import { Eyebrow } from '../atoms';
import { GradientGauge } from '../atoms';
import { getPerformanceLevel } from '../../charts/benchmarkThresholds';
import { StatementCard } from './StatementCard';

export interface DrawerProps {
  /** Currently-open node, or null when closed. */
  node: ScoreNode | null;
  onClose: () => void;
  /** Element description (ELEMENT_DESCRIPTIONS sidebar). Falls back to node.meaning. */
  description?: string;
  /** Element AI summary for "First Thoughts". Falls back to the awaiting message. */
  firstThoughts?: string;
  /** True while the AI summary is still loading — shows "Analysing…". */
  firstThoughtsLoading?: boolean;
  /** Industry benchmark for this element (when it's in the benchmark set) —
   *  shown beside the brand score in the header. */
  benchmark?: { industryMean: number; delta: number };
  /** Click the benchmark score → deep-link to The Benchmarks (relative section). */
  onBenchmarkClick?: () => void;
  /** Footer CTA — navigates to The Detail (orbital visualisation). */
  onViewInViz?: () => void;
  /** Re-run the AI "First Thoughts" — returns the new summary. */
  onAnalyseAgain?: () => Promise<string | undefined>;
}

const INFO = '#3B82F6';
const PRIMARY = '#F59E0B';

function formatScore(value: number | null): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(1)}%`;
}

export const Drawer: React.FunctionComponent<DrawerProps> = ({
  node,
  onClose,
  description,
  firstThoughts,
  firstThoughtsLoading,
  benchmark,
  onBenchmarkClick,
  onViewInViz,
  onAnalyseAgain,
}) => {
  const intl = useIntl();
  const open = node != null;
  // Price Deals lives on a -100..0 scale (zero = right edge), matching the
  // v3 orbital panel and SignalBar.
  const isPriceDeals = node?.canonical === 'Price Deals';

  // Local summary so "Analyse Again" can update it in place; reseed per node.
  const [summary, setSummary] = useState<string | undefined>(firstThoughts);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSummary(firstThoughts);
    setBusy(false);
  }, [firstThoughts, node]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleAgain = async (): Promise<void> => {
    if (!onAnalyseAgain || busy) return;
    setBusy(true);
    try {
      const next = await onAnalyseAgain();
      if (next) setSummary(next);
    } finally {
      setBusy(false);
    }
  };

  const showLoading = !summary && !!firstThoughtsLoading;
  const thoughts =
    summary ||
    intl.formatMessage({
      id: 'brandEquity.aiSummary.awaitingResponses',
      defaultMessage: 'Awaiting more responses before forming a view.',
    });

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,28,46,0.34)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity .25s',
          zIndex: 1000,
          backdropFilter: 'blur(2px)',
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: 'min(440px, 92vw)',
          background: '#fff',
          zIndex: 1001,
          boxShadow: '-20px 0 50px rgba(20,30,55,0.18)',
          transform: open ? 'translateX(0)' : 'translateX(102%)',
          transition: 'transform .32s cubic-bezier(.4,0,.1,1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {node && (
          <>
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                padding: '22px 24px',
                borderBottom: '1px solid #F1F5F9',
                flex: '0 0 auto',
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 700, color: '#0F172A' }}>
                {node.display}
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                  border: 'none',
                  background: 'none',
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  cursor: 'pointer',
                  color: '#94A3B8',
                  fontSize: 24,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '22px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span
                  style={{
                    fontSize: 42,
                    fontWeight: 800,
                    color: colourForScore(node.canonical, node.value),
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                  }}
                >
                  {formatScore(node.value)}
                </span>
                {node.classification && (
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: '#64748B',
                      textTransform: 'uppercase',
                    }}
                  >
                    {node.classification}
                  </span>
                )}
                {benchmark && (
                  <button
                    type="button"
                    onClick={onBenchmarkClick}
                    disabled={!onBenchmarkClick}
                    title={
                      onBenchmarkClick
                        ? intl.formatMessage({
                            id: 'marketerView.drawer.benchmarkLink',
                            defaultMessage: 'See this in The Benchmarks',
                          })
                        : undefined
                    }
                    style={{
                      marginLeft: 'auto',
                      alignSelf: 'flex-start',
                      textAlign: 'right',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 2,
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      font: 'inherit',
                      cursor: onBenchmarkClick ? 'pointer' : 'default',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#94A3B8',
                      }}
                    >
                      <FormattedMessage
                        id="marketerView.drawer.benchmark"
                        defaultMessage="Benchmark"
                      />
                    </span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#475569', lineHeight: 1 }}>
                      {formatScore(benchmark.industryMean)}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: getPerformanceLevel(benchmark.delta).color,
                      }}
                    >
                      {`${benchmark.delta >= 0 ? '+' : ''}${benchmark.delta.toFixed(1)}% `}
                      <FormattedMessage
                        id="marketerView.drawer.vsMarket"
                        defaultMessage="vs market"
                      />
                    </span>
                  </button>
                )}
              </div>

              <div style={{ margin: '18px 0 7px' }}>
                <GradientGauge value={node.value} negativeScale={isPriceDeals} />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#94A3B8',
                }}
              >
                {isPriceDeals ? (
                  <>
                    <span>-100%</span>
                    <span>0%</span>
                  </>
                ) : (
                  <>
                    <span>-100%</span>
                    <span>0</span>
                    <span>+100%</span>
                  </>
                )}
              </div>

              {(description || node.meaning) && (
                <p
                  style={{
                    fontSize: 14,
                    color: '#64748B',
                    lineHeight: 1.55,
                    margin: '18px 0 0',
                  }}
                >
                  {description || node.meaning}
                </p>
              )}

              {/* First Thoughts — element AI summary */}
              <div
                style={{
                  border: '1px solid #DBEAFE',
                  borderLeft: `3px solid ${INFO}`,
                  background: '#F8FAFF',
                  borderRadius: 12,
                  padding: '16px 18px',
                  marginTop: 18,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: INFO,
                    marginBottom: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                  }}
                >
                  <span style={{ fontSize: 14 }}>💭</span>
                  <FormattedMessage
                    id="marketerView.drawer.firstThoughts"
                    defaultMessage="First Thoughts"
                  />
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    color: '#64748B',
                    lineHeight: 1.6,
                    margin: onAnalyseAgain ? '0 0 14px' : 0,
                  }}
                >
                  {showLoading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <LoadingOutlined style={{ fontSize: 14, color: INFO }} />
                      <FormattedMessage
                        id="marketerView.drawer.analysing"
                        defaultMessage="Analysing…"
                      />
                    </span>
                  ) : (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <span style={{ display: 'block', marginBottom: 6 }}>{children}</span>
                        ),
                        strong: ({ children }) => (
                          <strong style={{ fontWeight: 700, color: '#0F172A' }}>{children}</strong>
                        ),
                        em: ({ children }) => <em>{children}</em>,
                        ul: ({ children }) => (
                          <ul style={{ paddingLeft: 16, margin: '4px 0' }}>{children}</ul>
                        ),
                        li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
                      }}
                    >
                      {thoughts}
                    </ReactMarkdown>
                  )}
                </div>
                {onAnalyseAgain && (
                  <button
                    type="button"
                    onClick={handleAgain}
                    disabled={busy}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      fontFamily: 'inherit',
                      fontSize: 13,
                      fontWeight: 600,
                      color: INFO,
                      background: '#EFF6FF',
                      border: '1px solid #BFDBFE',
                      borderRadius: 8,
                      padding: '8px 14px',
                      cursor: busy ? 'not-allowed' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    <ReloadOutlined spin={busy} style={{ fontSize: 14 }} />
                    {busy ? (
                      <FormattedMessage
                        id="marketerView.drawer.analysing"
                        defaultMessage="Analysing…"
                      />
                    ) : (
                      <FormattedMessage
                        id="marketerView.drawer.analyseAgain"
                        defaultMessage="Analyse Again"
                      />
                    )}
                  </button>
                )}
              </div>

              {node.statements && node.statements.length > 0 && (
                <div style={{ marginTop: 26 }}>
                  <Eyebrow>
                    <FormattedMessage
                      id="marketerView.drawer.statementsCount"
                      defaultMessage="Survey statements ({n})"
                      values={{ n: node.statements.length }}
                    />
                  </Eyebrow>
                  {node.statements.map((s, i) => (
                    <StatementCard key={`${s.text}-${i}`} statement={s} />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {onViewInViz && (
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '18px 24px',
                  borderTop: '1px solid #F1F5F9',
                  flex: '0 0 auto',
                }}
              >
                <button
                  type="button"
                  onClick={onViewInViz}
                  style={{
                    flex: 1,
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    background: PRIMARY,
                    border: 'none',
                    borderRadius: 8,
                    padding: 12,
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(245,158,11,.32)',
                  }}
                >
                  <FormattedMessage
                    id="marketerView.drawer.viewInViz"
                    defaultMessage="View in the visualisation →"
                  />
                </button>
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
};
